import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const TABS_RPT = [
  { id: 'comprobantes',  label: 'Comprobantes (detalle)' },
  { id: 'observaciones', label: 'Observaciones (RO)' },
  { id: 'visitas',       label: 'Visitas por cliente' },
  { id: 'emergencias',   label: 'Emergencias' },
  { id: 'presupuestos',  label: 'Presupuestos' },
  { id: 'cobertura',     label: 'Cobertura' },
];

const PRIO_LABEL = { alta: 'Alta', media: 'Media', baja: 'Baja' };
const PRIO_COLOR = { alta: '#E5533D', media: '#E5A93D', baja: '#38BDF8' };
const CAT_LABEL  = { critica: 'Crítica funcionamiento', normativa: 'Normativa' };

const ESTADO_COLORS = {
  enviado:   { bg: '#0D1E2E', color: '#60A5FA' },
  aceptado:  { bg: '#12382D', color: '#4CAF50' },
  rechazado: { bg: '#2D0F0F', color: '#EF4444' },
  borrador:  { bg: '#1A1F1D', color: '#8A9994' },
};

const hoy = new Date().toISOString().slice(0, 10);
const primerDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  .toISOString().slice(0, 10);

function fmtFecha(d) {
  if (!d) return '—';
  return new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CL');
}
function fmtMonto(n) {
  if (n == null) return '$0';
  return '$' + Math.round(Number(n)).toLocaleString('es-CL');
}

const INPUT_ST = {
  padding: '8px 10px', border: '1.5px solid #2E4A40', borderRadius: 8,
  fontSize: 13, background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
};

// ── Barra de filtros de fecha ─────────────────────────────────────────────────
function FiltrosFecha({ desde, hasta, onDesde, onHasta, onBuscar, loading, children }) {
  return (
    <div style={{ background: '#161C19', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
                  padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                        fontWeight: 600, color: '#9FAAA5' }}>
          Desde
          <input type="date" value={desde} onChange={e => onDesde(e.target.value)}
            style={INPUT_ST} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                        fontWeight: 600, color: '#9FAAA5' }}>
          Hasta
          <input type="date" value={hasta} onChange={e => onHasta(e.target.value)}
            style={INPUT_ST} />
        </label>
        {children}
        <button onClick={onBuscar} disabled={loading}
          style={{ padding: '9px 20px', background: loading ? '#1A2018' : '#0f6e56',
                   color: loading ? '#6B7C74' : 'white', border: 'none', borderRadius: 8,
                   fontWeight: 700, fontSize: 13, cursor: loading ? 'default' : 'pointer',
                   height: 38, fontFamily: 'inherit' }}>
          {loading ? 'Cargando…' : 'Generar'}
        </button>
      </div>
    </div>
  );
}

// ── Caja de resumen ───────────────────────────────────────────────────────────
function Stat({ label, value, color = '#4CAF50' }) {
  return (
    <div style={{ background: '#161C19', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10,
                  padding: '10px 18px', textAlign: 'center', minWidth: 100 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Tabla genérica ────────────────────────────────────────────────────────────
function Tabla({ cols, rows, empty = 'Sin resultados para el período seleccionado' }) {
  if (rows.length === 0) return (
    <p style={{ textAlign: 'center', color: '#6B7C74', padding: 32 }}>{empty}</p>
  );
  return (
    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#0f6e56' }}>
            {cols.map(c => (
              <th key={c.key} style={{ padding: '9px 12px', textAlign: 'left',
                                       whiteSpace: 'nowrap', fontWeight: 600, color: 'white' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#111714' : '#0D1311',
                                  borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '8px 12px', color: '#C9D3CE', ...(c.style || {}) }}>
                  {c.render ? c.render(row) : row[c.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: Comprobantes (detalle histórico descargable) ─────────────────────────
function TabComprobantes() {
  const [desde, setDesde]         = useState(primerDiaMes);
  const [hasta, setHasta]         = useState(hoy);
  const [comunidad, setComunidad] = useState('');
  const [tipo, setTipo]           = useState('');
  const [tipoVisita, setTipoVisita] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [datos, setDatos]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [buscado, setBuscado]     = useState(false);
  const [comunidades, setComunidades] = useState([]);
  const [tiposVisita, setTiposVisita] = useState([]);
  const [tecnicos, setTecnicos]   = useState([]);

  useEffect(() => {
    api.get('/reportes/comunidades').then(r => setComunidades(r.data || [])).catch(() => {});
    api.get('/reportes/tipos-visita').then(r => setTiposVisita(r.data || [])).catch(() => {});
    api.get('/admin/tecnicos').then(r => setTecnicos(r.data || [])).catch(() => {});
  }, []);

  const paramsActuales = () => ({
    desde, hasta,
    comunidad:   comunidad || undefined,
    tipo:        tipo || undefined,
    tipo_visita: tipoVisita || undefined,
    tecnico_id:  tecnicoId || undefined,
  });

  const buscar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reportes/comprobantes', { params: paramsActuales() });
      setDatos(r.data); setBuscado(true);
    } catch { alert('Error al generar el reporte'); }
    finally { setLoading(false); }
  };

  const descargar = async () => {
    setDescargando(true);
    try {
      const res = await api.get('/reportes/comprobantes/export', {
        params: paramsActuales(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'comprobantes.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al descargar el Excel'); }
    finally { setDescargando(false); }
  };

  const selSt = { ...INPUT_ST, height: 38 };
  const lblSt = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#9FAAA5' };

  const cols = [
    { key: 'folio', label: 'Folio', style: { fontWeight: 600, color: '#F4F7F5', whiteSpace: 'nowrap' } },
    { key: 'fecha_visita', label: 'Fecha', render: r => fmtFecha(r.fecha_visita) },
    { key: 'tipo', label: 'Tipo', render: r => r.tipo === 'mantenimiento' ? 'Mantenimiento' : 'Visita técnica' },
    { key: 'tipo_visita', label: 'Tipo visita', render: r => r.tipo_visita || '—' },
    { key: 'comunidad', label: 'Comunidad', style: { color: '#F4F7F5' } },
    { key: 'equipo', label: 'Equipo', render: r => `${r.label || ''}${r.n_ascensor ? ` · N°${r.n_ascensor}` : ''}${r.torre ? ` T${r.torre}` : ''}` || '—' },
    { key: 'tecnico', label: 'Técnico', render: r => r.tecnico || '—' },
    { key: 'estado_final_equipo', label: 'Estado equipo', render: r => r.estado_final_equipo || '—' },
    { key: 'estado_envio', label: 'Estado envío', render: r => ({
        enviado: 'Enviado', pendiente_aprobacion: 'Pendiente aprob.', rechazado: 'Rechazado',
      }[r.estado_envio] || r.estado_envio || '—') },
    { key: 'pdf', label: 'PDF', render: r => r.pdf_url
        ? <a href={r.pdf_url} target="_blank" rel="noreferrer" style={{ color: '#60A5FA' }}>Ver</a> : '—' },
  ];

  return (
    <div>
      <FiltrosFecha desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta}
        onBuscar={buscar} loading={loading}>
        <label style={lblSt}>
          Comunidad
          <select value={comunidad} onChange={e => setComunidad(e.target.value)} style={selSt}>
            <option value="">Todas</option>
            {comunidades.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={lblSt}>
          Tipo
          <select value={tipo} onChange={e => setTipo(e.target.value)} style={selSt}>
            <option value="">Todos</option>
            <option value="visita">Visita técnica</option>
            <option value="mantenimiento">Mantenimiento</option>
          </select>
        </label>
        <label style={lblSt}>
          Tipo de visita
          <select value={tipoVisita} onChange={e => setTipoVisita(e.target.value)} style={selSt}>
            <option value="">Todos</option>
            {tiposVisita.map(tv => <option key={tv} value={tv}>{tv}</option>)}
          </select>
        </label>
        <label style={lblSt}>
          Técnico
          <select value={tecnicoId} onChange={e => setTecnicoId(e.target.value)} style={selSt}>
            <option value="">Todos</option>
            {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </label>
      </FiltrosFecha>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {buscado && <Stat label="Comprobantes" value={datos.length} color="#4CAF50" />}
        <button onClick={descargar} disabled={descargando}
          style={{ padding: '10px 18px',
                   background: descargando ? '#1A2018' : '#0D1E2E',
                   color: descargando ? '#6B7C74' : '#60A5FA',
                   border: '1px solid #1E3A5F', borderRadius: 8, fontWeight: 700, fontSize: 13,
                   cursor: descargando ? 'default' : 'pointer', fontFamily: 'inherit' }}>
          {descargando ? 'Descargando…' : '⬇ Descargar Excel'}
        </button>
        <span style={{ fontSize: 12, color: '#6B7C74' }}>
          Usa "Generar" para previsualizar en pantalla, o descarga el Excel directamente con los filtros elegidos.
        </span>
      </div>

      {buscado && <Tabla cols={cols} rows={datos} empty="Sin comprobantes para los filtros seleccionados" />}
    </div>
  );
}

// ── Tab: Observaciones RO clasificadas (para presupuestos segmentados) ────────
function TabObservaciones() {
  const [desde, setDesde]         = useState(primerDiaMes);
  const [hasta, setHasta]         = useState(hoy);
  const [comunidad, setComunidad] = useState('');
  const [prioridad, setPrioridad] = useState('');
  const [categoria, setCategoria] = useState('');
  const [datos, setDatos]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [buscado, setBuscado]     = useState(false);
  const [comunidades, setComunidades] = useState([]);

  useEffect(() => {
    api.get('/reportes/comunidades').then(r => setComunidades(r.data || [])).catch(() => {});
  }, []);

  const paramsActuales = () => ({
    desde, hasta,
    comunidad: comunidad || undefined,
    prioridad: prioridad || undefined,
    categoria: categoria || undefined,
  });

  const buscar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reportes/observaciones', { params: paramsActuales() });
      setDatos(r.data); setBuscado(true);
    } catch { alert('Error al generar el reporte'); }
    finally { setLoading(false); }
  };

  const descargar = async () => {
    setDescargando(true);
    try {
      const res = await api.get('/reportes/observaciones/export', { params: paramsActuales(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'observaciones.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al descargar el Excel'); }
    finally { setDescargando(false); }
  };

  const selSt = { ...INPUT_ST, height: 38 };
  const lblSt = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#9FAAA5' };

  const cols = [
    { key: 'comunidad', label: 'Comunidad', style: { fontWeight: 600, color: '#F4F7F5' } },
    { key: 'equipo', label: 'Equipo', render: r => `${r.label || ''}${r.n_ascensor ? ` · N°${r.n_ascensor}` : ''}${r.torre ? ` T${r.torre}` : ''}` || '—' },
    { key: 'prioridad', label: 'Prioridad', render: r => (
        <span style={{ background: (PRIO_COLOR[r.prioridad] || '#555') + '22', color: PRIO_COLOR[r.prioridad] || '#8A9994',
                       borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
          {PRIO_LABEL[r.prioridad] || r.prioridad || '—'}
        </span>) },
    { key: 'categoria', label: 'Categoría', render: r => CAT_LABEL[r.categoria] || r.categoria || '—' },
    { key: 'descripcion', label: 'Ítem', style: { color: '#F4F7F5' } },
    { key: 'observacion', label: 'Observación', render: r => r.observacion || '—' },
    { key: 'fecha_visita', label: 'Fecha', render: r => fmtFecha(r.fecha_visita) },
    { key: 'tecnico', label: 'Técnico', render: r => r.tecnico || '—' },
  ];

  return (
    <div>
      <FiltrosFecha desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta}
        onBuscar={buscar} loading={loading}>
        <label style={lblSt}>
          Comunidad
          <select value={comunidad} onChange={e => setComunidad(e.target.value)} style={selSt}>
            <option value="">Todas</option>
            {comunidades.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={lblSt}>
          Prioridad
          <select value={prioridad} onChange={e => setPrioridad(e.target.value)} style={selSt}>
            <option value="">Todas</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
        </label>
        <label style={lblSt}>
          Categoría
          <select value={categoria} onChange={e => setCategoria(e.target.value)} style={selSt}>
            <option value="">Todas</option>
            <option value="critica">Crítica funcionamiento</option>
            <option value="normativa">Normativa</option>
          </select>
        </label>
      </FiltrosFecha>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {buscado && <Stat label="Observaciones RO" value={datos.length} color="#E5A93D" />}
        <button onClick={descargar} disabled={descargando}
          style={{ padding: '10px 18px', background: descargando ? '#1A2018' : '#0D1E2E',
                   color: descargando ? '#6B7C74' : '#60A5FA', border: '1px solid #1E3A5F',
                   borderRadius: 8, fontWeight: 700, fontSize: 13,
                   cursor: descargando ? 'default' : 'pointer', fontFamily: 'inherit' }}>
          {descargando ? 'Descargando…' : '⬇ Descargar Excel'}
        </button>
        <span style={{ fontSize: 12, color: '#6B7C74' }}>
          Filtra por prioridad para armar presupuestos separados (ej. solo "Alta").
        </span>
      </div>

      {buscado && <Tabla cols={cols} rows={datos} empty="Sin observaciones RO para los filtros seleccionados" />}
    </div>
  );
}

// ── Tab 1: Visitas por cliente ────────────────────────────────────────────────
function TabVisitas() {
  const [desde, setDesde]       = useState(primerDiaMes);
  const [hasta, setHasta]       = useState(hoy);
  const [comunidad, setComunidad] = useState('');
  const [datos, setDatos]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [expandidas, setExpandidas] = useState(new Set());

  const buscar = async () => {
    setLoading(true);
    setExpandidas(new Set());
    try {
      const r = await api.get('/reportes/visitas', { params: { desde, hasta, comunidad } });
      setDatos(r.data);
    } catch { /* sin datos */ }
    finally { setLoading(false); }
  };

  const totalVisitas = datos.reduce((s, d) => s + Number(d.total_visitas), 0);
  const conVisitas   = datos.filter(d => Number(d.total_visitas) > 0).length;

  return (
    <div>
      <FiltrosFecha desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta}
        onBuscar={buscar} loading={loading}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                        fontWeight: 600, color: '#9FAAA5' }}>
          Comunidad (opcional)
          <input value={comunidad} onChange={e => setComunidad(e.target.value)}
            placeholder="Filtrar por nombre…"
            style={{ ...INPUT_ST, width: 200 }} />
        </label>
      </FiltrosFecha>

      {datos.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <Stat label="Total visitas" value={totalVisitas} />
            <Stat label="Comunidades con visita" value={conVisitas} color="#60A5FA" />
            <Stat label="Total comunidades" value={datos.length} color="#8A9994" />
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#0f6e56' }}>
                  {['', 'Comunidad', 'Equipos', 'Mantenciones', 'Visitas Técnicas',
                    'Total', 'Última visita'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left',
                                         whiteSpace: 'nowrap', fontWeight: 600, color: 'white' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map((d, i) => {
                  const exp = expandidas.has(d.comunidad);
                  const detalle = d.detalle || [];
                  return (
                    <React.Fragment key={d.comunidad}>
                      <tr style={{ background: i % 2 === 0 ? '#111714' : '#0D1311',
                                   cursor: detalle.length ? 'pointer' : 'default',
                                   borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                          onClick={() => {
                            if (!detalle.length) return;
                            setExpandidas(prev => {
                              const n = new Set(prev);
                              n.has(d.comunidad) ? n.delete(d.comunidad) : n.add(d.comunidad);
                              return n;
                            });
                          }}>
                        <td style={{ padding: '8px 10px', width: 28, color: '#4CAF50' }}>
                          {detalle.length > 0 ? (exp ? '▼' : '▶') : ''}
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#F4F7F5' }}>
                          {d.comunidad}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#8A9994' }}>
                          {d.n_equipos}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center',
                                     color: Number(d.mantenimientos) > 0 ? '#4CAF50' : '#6B7C74',
                                     fontWeight: Number(d.mantenimientos) > 0 ? 700 : 400 }}>
                          {d.mantenimientos}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center',
                                     color: Number(d.visitas_tecnicas) > 0 ? '#60A5FA' : '#6B7C74' }}>
                          {d.visitas_tecnicas}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700,
                                     color: Number(d.total_visitas) > 0 ? '#F4F7F5' : '#6B7C74' }}>
                          {d.total_visitas}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#8A9994', fontSize: 12 }}>
                          {fmtFecha(d.ultima_visita)}
                        </td>
                      </tr>
                      {exp && detalle.map((v, j) => (
                        <tr key={v.id} style={{ background: '#0D1311',
                                               borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td />
                          <td style={{ padding: '5px 12px 5px 24px', fontSize: 12, color: '#6B7C74' }}>
                            └ {v.label}
                          </td>
                          <td colSpan={2} style={{ padding: '5px 12px', fontSize: 12, color: '#8A9994' }}>
                            <span style={{ background: v.tipo === 'mantenimiento' ? '#12382D' : '#0D1E2E',
                                           color: v.tipo === 'mantenimiento' ? '#4CAF50' : '#60A5FA',
                                           borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                              {v.tipo}
                            </span>
                            {v.tipo_visita && <span style={{ marginLeft: 6, color: '#6B7C74' }}>{v.tipo_visita}</span>}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#8A9994' }}>
                            {v.folio}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#C9D3CE' }}>
                            {fmtFecha(v.fecha_visita)}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#6B7C74' }}>
                            {v.tecnico || '—'}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 2: Emergencias ────────────────────────────────────────────────────────
function TabEmergencias() {
  const [desde, setDesde]     = useState(primerDiaMes);
  const [hasta, setHasta]     = useState(hoy);
  const [datos, setDatos]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroQ, setFiltroQ] = useState('');

  const buscar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reportes/emergencias', { params: { desde, hasta } });
      setDatos(r.data);
    } catch { /* sin datos */ }
    finally { setLoading(false); }
  };

  const visibles = filtroQ
    ? datos.filter(d => d.comunidad?.toLowerCase().includes(filtroQ.toLowerCase()))
    : datos;

  const comunidadesAfectadas = new Set(visibles.map(d => d.comunidad)).size;

  return (
    <div>
      <FiltrosFecha desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta}
        onBuscar={buscar} loading={loading} />

      {datos.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap',
                        alignItems: 'center' }}>
            <Stat label="Total emergencias" value={visibles.length} color="#EF4444" />
            <Stat label="Comunidades afectadas" value={comunidadesAfectadas} color="#F59E0B" />
            <input value={filtroQ} onChange={e => setFiltroQ(e.target.value)}
              placeholder="Filtrar por comunidad…"
              style={{ ...INPUT_ST, marginLeft: 'auto', width: 200 }} />
          </div>

          <Tabla
            cols={[
              { key:'fecha_visita', label:'Fecha',     render: r => fmtFecha(r.fecha_visita) },
              { key:'comunidad',    label:'Comunidad', style:{ fontWeight:600, color:'#F4F7F5' } },
              { key:'label',        label:'Equipo',    style:{ fontSize:12 } },
              { key:'tecnico',      label:'Técnico',   style:{ fontSize:12 } },
              { key:'hora_emergencia', label:'Hora emergencia',
                render: r => r.hora_emergencia || r.hora_ingreso || '—',
                style:{ fontSize:12 } },
              { key:'estado_final_equipo', label:'Estado equipo',
                render: r => r.estado_final_equipo || '—', style:{ fontSize:12 } },
              { key:'resumen_visita', label:'Resumen',
                render: r => (
                  <span title={r.resumen_visita || r.observaciones_generales}
                    style={{ display:'block', maxWidth:280, overflow:'hidden',
                             textOverflow:'ellipsis', whiteSpace:'nowrap',
                             fontSize:12, color:'#8A9994' }}>
                    {r.resumen_visita || r.observaciones_generales || '—'}
                  </span>
                )},
            ]}
            rows={visibles}
          />
        </>
      )}
    </div>
  );
}

// ── Tab 3: Presupuestos ───────────────────────────────────────────────────────
function TabPresupuestos() {
  const [desde, setDesde]     = useState(primerDiaMes);
  const [hasta, setHasta]     = useState(hoy);
  const [estado, setEstado]   = useState('todos');
  const [datos, setDatos]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroQ, setFiltroQ] = useState('');
  const [expandidos, setExpandidos] = useState(new Set());

  const buscar = async () => {
    setLoading(true);
    setExpandidos(new Set());
    try {
      const r = await api.get('/reportes/presupuestos', { params: { desde, hasta, estado } });
      setDatos(r.data);
    } catch { /* sin datos */ }
    finally { setLoading(false); }
  };

  const visibles = filtroQ
    ? datos.filter(d => d.comunidad?.toLowerCase().includes(filtroQ.toLowerCase()) ||
                        d.numero?.toLowerCase().includes(filtroQ.toLowerCase()))
    : datos;

  const totalMonto = visibles.reduce((s, d) => s + Number(d.total || 0), 0);
  const aceptados  = visibles.filter(d => d.estado === 'aceptado').length;

  const toggleExp = (id) => setExpandidos(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  return (
    <div>
      <FiltrosFecha desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta}
        onBuscar={buscar} loading={loading}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                        fontWeight: 600, color: '#9FAAA5' }}>
          Estado
          <select value={estado} onChange={e => setEstado(e.target.value)}
            style={{ ...INPUT_ST, height: 38 }}>
            <option value="todos">Todos los estados</option>
            <option value="enviado">Enviado</option>
            <option value="aceptado">Aceptado</option>
            <option value="rechazado">Rechazado</option>
          </select>
        </label>
      </FiltrosFecha>

      {datos.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap',
                        alignItems: 'center' }}>
            <Stat label="Total presupuestos" value={visibles.length} />
            <Stat label="Aceptados" value={aceptados} color="#4CAF50" />
            <Stat label="Monto total" value={fmtMonto(totalMonto)} color="#60A5FA" />
            <input value={filtroQ} onChange={e => setFiltroQ(e.target.value)}
              placeholder="Filtrar comunidad o N°…"
              style={{ ...INPUT_ST, marginLeft: 'auto', width: 200 }} />
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#0f6e56' }}>
                  {['', 'N° Presupuesto', 'Comunidad', 'Tipo', 'Fecha', 'Estado', 'Total'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left',
                                         whiteSpace: 'nowrap', fontWeight: 600, color: 'white' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibles.map((p, i) => {
                  const estColor = ESTADO_COLORS[p.estado] || ESTADO_COLORS.borrador;
                  const exp = expandidos.has(p.id);
                  const items = p.items || [];
                  return (
                    <React.Fragment key={p.id}>
                      <tr style={{ background: i % 2 === 0 ? '#111714' : '#0D1311',
                                   cursor: items.length ? 'pointer' : 'default',
                                   borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                          onClick={() => items.length && toggleExp(p.id)}>
                        <td style={{ padding: '8px 10px', width: 28, color: '#4CAF50' }}>
                          {items.length > 0 ? (exp ? '▼' : '▶') : ''}
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 700,
                                     color: '#4CAF50', fontFamily: 'monospace', fontSize: 12 }}>
                          {p.numero}
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#F4F7F5' }}>{p.comunidad}</td>
                        <td style={{ padding: '8px 12px', color: '#8A9994', fontSize: 12 }}>{p.tipo}</td>
                        <td style={{ padding: '8px 12px', fontSize: 12, color: '#8A9994' }}>
                          {fmtFecha(p.enviado_at || p.created_at)}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ ...estColor, borderRadius: 6, padding: '2px 8px',
                                         fontSize: 11, fontWeight: 700 }}>
                            {p.estado}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#60A5FA' }}>
                          {fmtMonto(p.total)}
                        </td>
                      </tr>
                      {exp && items.map((it, j) => (
                        <tr key={j} style={{ background: '#0D1311',
                                             borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td />
                          <td colSpan={3} style={{ padding: '5px 12px 5px 24px', fontSize: 12, color: '#8A9994' }}>
                            └ {it.descripcion}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#6B7C74', textAlign: 'center' }}>
                            {it.cantidad} {it.unidad}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#6B7C74' }}>
                            {fmtMonto(it.precio_unitario)} c/u
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#60A5FA' }}>
                            {fmtMonto(it.subtotal)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 4: Cobertura de mantenciones ─────────────────────────────────────────
function TabCobertura() {
  const [dias, setDias]       = useState(60);
  const [tipo, setTipo]       = useState('ambos');
  const [datos, setDatos]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroQ, setFiltroQ] = useState('');

  const buscar = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reportes/cobertura', { params: { dias, tipo } });
      setDatos(r.data);
    } catch { /* sin datos */ }
    finally { setLoading(false); }
  };

  const visibles = filtroQ
    ? datos.filter(d => d.comunidad?.toLowerCase().includes(filtroQ.toLowerCase()))
    : datos;

  const colorDias = (n) => {
    if (n == null) return { bg:'#2D0F0F', color:'#EF4444' };
    if (n > 90)    return { bg:'#2D0F0F', color:'#EF4444' };
    if (n > 60)    return { bg:'#1C1200', color:'#F59E0B' };
    return               { bg:'#1C1200', color:'#F59E0B' };
  };

  const sinVisita = visibles.filter(d => !d.ultima_visita).length;
  const masde90   = visibles.filter(d => Number(d.dias_sin_visita) > 90).length;

  return (
    <div>
      <div style={{ background: '#161C19', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
                    padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                          fontWeight: 600, color: '#9FAAA5' }}>
            Sin visita hace más de
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" value={dias} min={1} max={365}
                onChange={e => setDias(parseInt(e.target.value) || 30)}
                style={{ ...INPUT_ST, width: 72, textAlign: 'center' }} />
              <span style={{ fontSize: 13, color: '#9FAAA5' }}>días</span>
            </div>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[30,60,90].map(d => (
              <button key={d} onClick={() => setDias(d)}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid',
                         borderColor: dias === d ? '#4CAF50' : '#2E4A40',
                         background: dias === d ? '#12382D' : '#0D1311',
                         color: dias === d ? '#4CAF50' : '#8A9994',
                         fontSize: 12, fontWeight: 600, cursor: 'pointer',
                         fontFamily: 'inherit' }}>
                {d}d
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                          fontWeight: 600, color: '#9FAAA5' }}>
            Tipo formulario
            <select value={tipo} onChange={e => setTipo(e.target.value)}
              style={{ ...INPUT_ST, height: 38 }}>
              <option value="ambos">Ambos</option>
              <option value="mantenimiento">Solo mantención</option>
              <option value="visita">Solo visita técnica</option>
            </select>
          </label>
          <button onClick={buscar} disabled={loading}
            style={{ padding: '9px 20px', background: loading ? '#1A2018' : '#0f6e56',
                     color: loading ? '#6B7C74' : 'white', border: 'none', borderRadius: 8,
                     fontWeight: 700, fontSize: 13, cursor: loading ? 'default' : 'pointer',
                     height: 38, fontFamily: 'inherit' }}>
            {loading ? 'Cargando…' : 'Generar'}
          </button>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6B7C74' }}>
          Muestra ascensores activos sin formulario {tipo === 'ambos' ? 'de ningún tipo' : `de tipo "${tipo}"`} en los últimos {dias} días.
          Útil para verificar cumplimiento del contrato de mantención.
        </p>
      </div>

      {datos.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap',
                        alignItems: 'center' }}>
            <Stat label="Equipos sin visita" value={visibles.length} color="#EF4444" />
            <Stat label="Sin visita nunca" value={sinVisita} color="#EF4444" />
            <Stat label="Más de 90 días" value={masde90} color="#F59E0B" />
            <input value={filtroQ} onChange={e => setFiltroQ(e.target.value)}
              placeholder="Filtrar por comunidad…"
              style={{ ...INPUT_ST, marginLeft: 'auto', width: 200 }} />
          </div>

          <Tabla
            cols={[
              { key:'comunidad', label:'Comunidad', style:{ fontWeight:600, color:'#F4F7F5' } },
              { key:'label',     label:'Equipo',    style:{ fontSize:12 } },
              { key:'n_ascensor', label:'N°', style:{ textAlign:'center', fontSize:12 } },
              { key:'torre',     label:'Torre', style:{ textAlign:'center', fontSize:12 } },
              { key:'codigo',    label:'Código', style:{ fontFamily:'monospace', fontSize:11, color:'#4CAF50' } },
              { key:'ultima_visita', label:'Última visita',
                render: r => fmtFecha(r.ultima_visita),
                style:{ fontSize:12, color:'#8A9994' } },
              { key:'dias_sin_visita', label:'Días sin visita',
                render: r => {
                  const n = r.ultima_visita ? Number(r.dias_sin_visita) : null;
                  const c = colorDias(n);
                  return (
                    <span style={{ ...c, borderRadius:6, padding:'2px 10px',
                                   fontSize:12, fontWeight:700 }}>
                      {n == null ? 'Sin visitas' : `${n} días`}
                    </span>
                  );
                }},
            ]}
            rows={visibles}
          />
        </>
      )}
    </div>
  );
}

// ── Componente principal exportado ────────────────────────────────────────────
export default function TabReportes() {
  const [tab, setTab] = useState('comprobantes');

  return (
    <div>
      {/* Navegación de tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, flexWrap: 'wrap',
                    borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 0 }}>
        {TABS_RPT.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '9px 16px', border: 'none', borderRadius: '8px 8px 0 0',
              background: tab === t.id ? '#12382D' : 'transparent',
              color: tab === t.id ? '#4CAF50' : '#8A9994',
              fontWeight: tab === t.id ? 700 : 500,
              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              borderBottom: tab === t.id ? '2px solid #4CAF50' : '2px solid transparent',
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'comprobantes' && <TabComprobantes />}
      {tab === 'observaciones' && <TabObservaciones />}
      {tab === 'visitas'      && <TabVisitas />}
      {tab === 'emergencias'  && <TabEmergencias />}
      {tab === 'presupuestos' && <TabPresupuestos />}
      {tab === 'cobertura'    && <TabCobertura />}
    </div>
  );
}
